
import { useEffect } from 'react';
import { MonitoringEventLogger } from './types';

export const useTabFocusMonitoring = (
  logEvent: MonitoringEventLogger,
  currentQuestionId?: string
) => {
  useEffect(() => {
    let lastVisibilityChange = Date.now();
    let lastBlur = Date.now();

    const handleVisibilityChange = () => {
      const now = Date.now();
      const duration = now - lastVisibilityChange;
      const id = crypto.randomUUID();
      
      console.log(`[TabMonitoring] Visibility change detected. Hidden: ${document.hidden}, Duration: ${duration}ms`);
      
      if (document.hidden) {
        logEvent({
          id,
          event_type: 'tab_switch',
          event_data: { 
            action: 'leave',
            timestamp: new Date().toISOString()
          },
          severity: 2,
          created_at: new Date().toISOString(),
          duration: 0,
          question_id: currentQuestionId
        });
      } else {
        // Updated severity logic based on your requirements
        let severity = 1; // Default to low severity
        
        if (duration < 100) { // Less than 100ms (very fast, potentially automated)
          severity = 3; // High severity - likely automated/programmatic
        } else if (duration >= 100 && duration < 1000) { // 100ms to 1 second
          severity = 1; // Low severity
        } else if (duration >= 1000 && duration < 4000) { // 1-4 seconds
          severity = 2; // Medium severity
        } else { // 4 seconds or more
          severity = 3; // High severity
        }
        
        logEvent({
          id,
          event_type: 'tab_switch',
          event_data: { 
            action: 'return',
            timestamp: new Date().toISOString(),
            duration_ms: duration
          },
          severity,
          created_at: new Date().toISOString(),
          duration,
          question_id: currentQuestionId
        });
      }
      
      lastVisibilityChange = now;
    };

    const handleBlur = () => {
      const id = crypto.randomUUID();
      lastBlur = Date.now();
      console.log('[TabMonitoring] Window blur detected');
      
      logEvent({
        id,
        event_type: 'window_blur',
        event_data: { 
          action: 'blur',
          timestamp: new Date().toISOString()
        },
        severity: 2,
        created_at: new Date().toISOString(),
        duration: 0,
        question_id: currentQuestionId
      });
    };

    const handleFocus = () => {
      const now = Date.now();
      const duration = now - lastBlur;
      const id = crypto.randomUUID();
      
      console.log(`[TabMonitoring] Window focus detected. Duration: ${duration}ms`);
      
      // Updated severity logic for window focus events based on your requirements
      let severity = 1; // Default to low severity
      
      if (duration < 100) { // Less than 100ms (very fast)
        severity = 3; // High severity - potentially robotic
      } else if (duration >= 100 && duration < 1000) { // 100ms to 1 second
        severity = 1; // Low severity
      } else if (duration >= 1000 && duration < 4000) { // 1-4 seconds
        severity = 2; // Medium severity
      } else { // 4 seconds or more
        severity = 3; // High severity
      }
      
      logEvent({
        id,
        event_type: 'window_blur',
        event_data: { 
          action: 'focus',
          timestamp: new Date().toISOString(),
          duration_ms: duration
        },
        severity,
        created_at: new Date().toISOString(),
        duration,
        question_id: currentQuestionId
      });
    };

    console.log('[TabMonitoring] Setting up visibility and focus event listeners');
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      console.log('[TabMonitoring] Cleaning up visibility and focus event listeners');
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [logEvent, currentQuestionId]);
};
